import React, { useEffect, useId, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    FormControlLabel,
    Link,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import AppDialog from '../../dialog/AppDialog'

export const IMPORT_MODES = { APPEND: 'append', REPLACE: 'replace' }
export const ACCEPTED_EXTENSIONS = ['xlsx', 'xls', 'csv']

const MODE_OPTIONS = [
    {
        value: IMPORT_MODES.APPEND,
        label: 'Add new rows',
        description: 'Adds new keys without deleting rows. For Units, Wheels, Fans, Airflows and Options, changed Varcond or Kmat updates the existing row and its Old price, and clears its current price until SAP sync. Otherwise existing keys are ignored. QM only adds new keys.',
    },
    {
        value: IMPORT_MODES.REPLACE,
        label: 'Replace the whole table',
        description: 'Deletes every current row, then imports the rows of the file.',
    },
]

const optionStyles = (selected, danger) => ({
    m: 0,
    px: 1.5,
    py: 1,
    alignItems: 'flex-start',
    border: 1,
    borderRadius: 1,
    borderColor: selected ? (danger ? 'error.main' : 'info.main') : 'divider',
    bgcolor: (theme) => (selected ? alpha(theme.palette[danger ? 'error' : 'info'].main, 0.06) : 'transparent'),
    '& .MuiRadio-root': { mt: -0.5 },
})

export const hasAcceptedExtension = (fileName = '') =>
    ACCEPTED_EXTENSIONS.includes(fileName.split('.').pop())

const rulesStyles = { m: 0, pl: 2.5, '& li': { mb: 1 } }

export default function ImportModeDialog({ open, tableLabel, onClose, onConfirm, onShowHelp, infoOnly = false, api = '' }) {
    const modeLabelId = useId()
    const [mode, setMode] = useState(IMPORT_MODES.APPEND)
    const [file, setFile] = useState(null)
    const [fileError, setFileError] = useState('')
    const replacing = mode === IMPORT_MODES.REPLACE
    const destination = api.replace(/\/+$/, '').split('/').pop().toLowerCase()
    const importsSapMetadata = ['units', 'wheels', 'fans', 'airflows', 'options'].includes(destination)
    const rules = replacing
        ? [
            'All existing rows in this table are deleted.',
            'The valid rows from your file become the new table. Rows missing from the file are removed.',
            importsSapMetadata
                ? 'Old price is imported. Current SAP prices start empty: run Sync all rooftops prices afterwards.'
                : 'Prices are imported from the file.',
            'Other tables are not changed.',
        ]
        : [
            'New pricing keys are added. Rows not included in your file are kept.',
            ...(importsSapMetadata ? [
                'Same pricing key, same Varcond and Kmat: the row is skipped. Both existing prices stay unchanged.',
                'Same pricing key, different Varcond or Kmat: the existing row is updated, not duplicated. The uploaded Old price replaces the stored Old price.',
                'Updated rows have their current SAP price cleared. Run Sync all rooftops prices to calculate it again.',
            ] : ['Existing pricing keys are skipped. Existing rows and prices stay unchanged.']),
            'If a pricing key appears more than once in the file, only the first valid occurrence is processed.',
        ]

    useEffect(() => {
        if (!open) return
        setMode(IMPORT_MODES.APPEND)
        setFile(null)
        setFileError('')
    }, [open])

    const selectFile = (event) => {
        const selected = event.target.files?.[0]
        event.target.value = null
        if (!selected) return
        if (!hasAcceptedExtension(selected.name)) {
            setFile(null)
            setFileError('Invalid file: select an .xlsx, .xls or .csv file.')
            return
        }
        setFileError('')
        setFile(selected)
    }

    return (
        <AppDialog
            open={open}
            onClose={onClose}
            title={`${infoOnly ? 'How price upload works' : 'Upload new prices'} – ${tableLabel}`}
            maxWidth='sm'
            showCloseButton
            actions={
                infoOnly ? <Button onClick={onClose}>Close</Button> : <>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        variant='contained'
                        color={replacing ? 'error' : 'info'}
                        disabled={!file}
                        onClick={() => onConfirm(file, mode)}>
                        {replacing ? 'Replace table' : 'Add rows'}
                    </Button>
                </>
            }>
            <Stack spacing={2.5}>
                <Box>
                    <Typography component='h3' variant='subtitle2' id={modeLabelId} gutterBottom>
                        {infoOnly ? 'Choose a mode to see what happens' : '1. What should the import do?'}
                    </Typography>
                    <RadioGroup aria-labelledby={modeLabelId} value={mode} onChange={(e) => setMode(e.target.value)} sx={{ gap: 1 }}>
                        {MODE_OPTIONS.map((option) => (
                            <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={<Radio color={option.value === IMPORT_MODES.REPLACE ? 'error' : 'info'} />}
                                sx={optionStyles(mode === option.value, option.value === IMPORT_MODES.REPLACE)}
                                label={
                                    <Box>
                                        <Typography fontWeight={600}>{option.label}</Typography>
                                        <Typography variant='body2' color='text.secondary'>
                                            {infoOnly && option.value === IMPORT_MODES.APPEND
                                                ? importsSapMetadata ? 'Keep existing rows. Add new keys or update changed SAP metadata.' : 'Keep existing rows and add new pricing keys.'
                                                : option.description}
                                        </Typography>
                                    </Box>
                                }
                            />
                        ))}
                    </RadioGroup>
                    {replacing && (
                        <Alert severity='warning' sx={{ mt: 1.5 }}>
                            Rows absent from the file will be permanently removed. Download current prices first to keep a
                            backup.
                        </Alert>
                    )}
                </Box>

                {infoOnly ? (
                    <Box>
                        <Typography component='h3' variant='subtitle2' gutterBottom>
                            {replacing ? 'Replace rules' : 'Append rules'}
                        </Typography>
                        <Box component='ul' sx={rulesStyles}>
                            {rules.map((rule) => <Typography component='li' variant='body2' key={rule}>{rule}</Typography>)}
                        </Box>
                        <Typography variant='body2' color='text.secondary'>
                            This is help only. No file is uploaded and no data is changed.
                        </Typography>
                    </Box>
                ) : <Box>
                    <Typography component='h3' variant='subtitle2' gutterBottom>
                        2. Choose the file
                    </Typography>
                    <Stack direction='row' spacing={1.5} alignItems='center' flexWrap='wrap' useFlexGap>
                        <Button component='label' variant='outlined' color='inherit' startIcon={<AttachFileIcon />}>
                            {file ? 'Change file' : 'Choose file'}
                            <input type='file' hidden accept='.xlsx,.xls,.csv' aria-label='Price file' onChange={selectFile} />
                        </Button>
                        <Typography variant='body2' color={file ? 'text.primary' : 'text.secondary'} noWrap sx={{ minWidth: 0 }}>
                            {file ? file.name : 'No file selected (.xlsx, .xls or .csv)'}
                        </Typography>
                    </Stack>
                    {fileError && (
                        <Alert severity='error' sx={{ mt: 1.5 }}>
                            {fileError}
                        </Alert>
                    )}
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 1.5 }}>
                        The first row is treated as the header. Need the expected columns?{' '}
                        <Link component='button' type='button' variant='body2' onClick={onShowHelp}>
                            File format help
                        </Link>
                    </Typography>
                </Box>}
            </Stack>
        </AppDialog>
    )
}
